import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { defaultThemeName, type ColorMode, type ThemeName } from '@/theme/theme';

interface UiState {
  /** Active colour mode for the MUI theme. */
  colorMode: ColorMode;
  /** Which of the selectable looks is active (see themeCatalogue). */
  themeName: ThemeName;
  /** Whether the navigation sidebar is expanded (desktop). */
  sidebarOpen: boolean;
  /** Which sidebar groups the person has opened or closed, by group key; a group not listed follows the current page. */
  navGroupsOpen: Record<string, boolean>;
  toggleColorMode: () => void;
  setColorMode: (mode: ColorMode) => void;
  setThemeName: (name: ThemeName) => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setNavGroupOpen: (key: string, open: boolean) => void;
}

/**
 * Global UI store (theme, colour mode, sidebar state and which navigation groups are open),
 * persisted to localStorage so the user's choices survive reloads. A store persisted before a
 * field existed simply takes the default.
 */
export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      colorMode: 'light',
      themeName: defaultThemeName,
      sidebarOpen: true,
      navGroupsOpen: {},
      toggleColorMode: () =>
        set((state) => ({ colorMode: state.colorMode === 'light' ? 'dark' : 'light' })),
      setColorMode: (mode) => set({ colorMode: mode }),
      setThemeName: (name) => set({ themeName: name }),
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      setNavGroupOpen: (key, open) => set((state) => ({ navGroupsOpen: { ...state.navGroupsOpen, [key]: open } })),
    }),
    { name: 'zsic-sbp-ui' },
  ),
);
